import React from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/utils';
import Text from './Text';

/**
 * The metric tile from the reference's dashboard row: a tiny icon and label on
 * one line, then the number, then how it moved.
 *
 * `delta` is signed and interpreted, not just displayed — `invertDelta` is for
 * metrics where up is bad (cancellations, refunds), so the colour tracks
 * *good/bad* rather than *up/down*. Getting that backwards is the classic way
 * a dashboard quietly lies.
 */

export interface StatTileProps {
  icon?: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
  /** Percentage change. Positive renders as a rise. */
  delta?: number;
  /** Set when a rise in this metric is bad news. */
  invertDelta?: boolean;
  /** Replaces the delta with free-form supporting text. */
  hint?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

const StatTile: React.FC<StatTileProps> = ({
  icon,
  label,
  value,
  delta,
  invertDelta = false,
  hint,
  onClick,
  className,
}) => {
  const rising = typeof delta === 'number' && delta > 0;
  const good = invertDelta ? !rising : rising;
  const DeltaIcon = rising ? TrendingUp : TrendingDown;

  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={
        onClick
          ? (e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick())
          : undefined
      }
      className={cn(
        'p-4 rounded-flup-lg bg-flup-surface border border-flup-hairline',
        onClick && 'cursor-pointer transition-colors hover:border-flup-hairline-2 hover:bg-flup-surface-alt',
        className,
      )}
    >
      <div className="flex items-center gap-1.5 mb-2.5 min-w-0">
        {icon ? <span className="text-flup-ink-3 shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5">{icon}</span> : null}
        <Text variant="fieldLabel" tone="tertiary" as="p" lines={1}>
          {label}
        </Text>
      </div>

      <div className="flex items-end justify-between gap-2 flex-wrap">
        <Text variant="stat" as="p">
          {value}
        </Text>

        {typeof delta === 'number' ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 pb-1',
              good ? 'text-flup-success' : 'text-flup-danger',
            )}
          >
            <DeltaIcon className="w-3.5 h-3.5" aria-hidden />
            <Text variant="micro" tone="inherit" as="span" className="nums">
              {Math.abs(delta)}%
            </Text>
          </span>
        ) : hint ? (
          <Text variant="micro" tone="tertiary" as="span" className="pb-1">
            {hint}
          </Text>
        ) : null}
      </div>
    </div>
  );
};

/**
 * The logip variant: a circular icon well, the label, then the number — used in
 * the dashboard's greeting row where the tiles sit inline rather than in a grid.
 */
export const StatWell: React.FC<{
  icon: React.ReactNode;
  label: React.ReactNode;
  value: React.ReactNode;
  delta?: number;
  invertDelta?: boolean;
  hint?: React.ReactNode;
  className?: string;
}> = ({ icon, label, value, delta, invertDelta = false, hint, className }) => {
  const rising = typeof delta === 'number' && delta > 0;
  const good = invertDelta ? !rising : rising;
  const DeltaIcon = rising ? TrendingUp : TrendingDown;

  return (
    <div className={cn('flex items-center gap-3 min-w-0', className)}>
      <span className="grid place-items-center w-11 h-11 rounded-pill bg-flup-surface-alt text-flup-ink shrink-0 [&_svg]:w-[18px] [&_svg]:h-[18px]">
        {icon}
      </span>
      <div className="min-w-0">
        <Text variant="caption" tone="tertiary" as="p" lines={1}>
          {label}
        </Text>
        <div className="flex items-baseline gap-1.5">
          <Text variant="statSm" as="p">
            {value}
          </Text>
          {typeof delta === 'number' ? (
            <span
              className={cn(
                'inline-flex items-center gap-0.5',
                good ? 'text-flup-success' : 'text-flup-danger',
              )}
            >
              <DeltaIcon className="w-3 h-3" aria-hidden />
              <Text variant="micro" tone="inherit" as="span" className="nums">
                {Math.abs(delta)}%
              </Text>
            </span>
          ) : hint ? (
            <Text variant="micro" tone="tertiary" as="span">
              {hint}
            </Text>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default React.memo(StatTile);
